import { hexToRgba } from "@/utils/pieces";
import { text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { Chip, ChipProps, Typography, css, styled } from "@mui/material";
import { FC } from "react";

export type TTagVariant = "bold" | "light" | "outline";

export type TTagProps = {
  variant?: TTagVariant;
  color: string;
  textColor?: string;
  title: string;
  fontWeight?: number;
  minWidth?: string;
} & Omit<ChipProps, "variant" | "color">;

export const Tag: FC<TTagProps> = ({ variant = "bold", color, title, textColor, fontWeight, minWidth = "76px", ...rest }) => {
  return (
    <StyledChip
      chipVariant={variant}
      backgroundColor={color}
      minWidth={minWidth}
      label={
        <StyledTypography>
          {variant === "light" && <StyledPoint background={color} />}
          <Typography variant="cap12" style={{ color: textColor, fontWeight: fontWeight }}>
            {title}
          </Typography>
        </StyledTypography>
      }
      variant={variant === "outline" ? "outlined" : "filled"}
      {...rest}
    />
  );
};

const options = { shouldForwardProp: (propName: string) => !["chipVariant", "backgroundColor", "minWidth"].includes(propName) };

const getTagCSS = (variant: TTagVariant) => {
  switch (variant) {
    case "bold":
      return BoldTagCSS;
    case "outline":
      return OutlineTagCSS;
    default:
      return LightTagCSS;
  }
};

const getBackgroundColor = (variant: TTagVariant, color: string) => {
  switch (variant) {
    case "bold":
      return color;
    case "outline":
      return "transparent";
    default:
      return hexToRgba(color, 0.2);
  }
};

const StyledChip = styled(Chip, options)<{ chipVariant: TTagVariant; backgroundColor: string; minWidth: string }>`
  ${({ chipVariant }) => getTagCSS(chipVariant)};
  background-color: ${({ chipVariant, backgroundColor }) => getBackgroundColor(chipVariant, backgroundColor)};
  min-width: ${({ minWidth }) => minWidth};
  .MuiChip-label {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
  .MuiTypography-root {
    padding: 0;
  }
`;

const BoldTagCSS = css`
  border-radius: ${rounded.md};
  .MuiTypography-root {
    font-size: 12px;
    font-weight: bold;
    color: ${text.white};
  }
`;

const LightTagCSS = css`
  border-radius: ${rounded.lg};
  .MuiTypography-root {
    font-size: 14px;
    color: ${text.primary};
  }
`;

const OutlineTagCSS = css`
  border-radius: ${rounded.lg};
  .MuiTypography-root {
    font-size: 12px;
    color: ${text.primary};
  }
`;

const StyledTypography = styled("span")`
  display: inline-flex;
  align-items: center;
  column-gap: 8px;
  ${css(typography.body14)}
`;

const StyledPoint = styled("span")<{ background: string }>`
  width: 8px;
  height: 8px;
  border-radius: ${rounded.full};
  background-color: ${({ background }) => background};
`;
